import React, {useContext, useEffect, useMemo, useState} from "react"
import {LoadingButton} from "@mui/lab"
import {
    Alert,
    Button,
    CircularProgress,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import {useGetAiGlossaryQuery, useSaveAiGlossaryMutation} from "../../core/api/ai-zendesk-settings-api"
import {AI_GAME_LABELS, errorText, formatExact} from "../../components/AiTickets/aiTicketsUtils"
import {SnackBarContext} from "../../snackBarProvider"

const emptyTerm = () => ({term: "", en: "", note: ""})

const toRows = (terms = []) => terms.map((t) => ({term: t.term ?? "", en: t.en ?? "", note: t.note ?? ""}))

const sameRows = (a, b) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Game glossary: how players call things (any language) -> official English name from the knowledge base.
 * The bot's analyzer uses it to restate the player's question in the knowledge base wording.
 */
export default function GlossaryEditor({initialGame = "toh"}) {
    const {addAlert} = useContext(SnackBarContext)
    const [game, setGame] = useState(initialGame)
    const {data, isFetching, error} = useGetAiGlossaryQuery(game)
    const [saveGlossary, {isLoading: saving}] = useSaveAiGlossaryMutation()
    const [rows, setRows] = useState([])

    const loaded = useMemo(() => toRows(data?.terms), [data])
    useEffect(() => setRows(loaded), [loaded])

    const dirty = !sameRows(rows, loaded)
    const invalid = rows.some((r) => !r.term.trim() || !r.en.trim())

    const changeGame = (next) => {
        if (dirty && !window.confirm("Discard unsaved glossary changes?")) return
        setGame(next)
    }

    const update = (index, field, value) =>
        setRows((current) => current.map((row, i) => (i === index ? {...row, [field]: value} : row)))

    const save = async () => {
        const terms = rows.map((r) => ({term: r.term.trim(), en: r.en.trim(), note: r.note.trim() || null}))
        try {
            await saveGlossary({game, terms}).unwrap()
            addAlert({text: `Glossary saved: ${terms.length} terms`, type: "success"})
        } catch (e) {
            addAlert({text: errorText(e), type: "error"})
        }
    }

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                How players name game things in their own words and languages, and the official English name used in the
                knowledge base. Example: "штурм босса / клановый штурм" = "Boss Strike". Put several spellings into one line
                separated by "/". Changes apply to the next player message.
            </Typography>
            <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                <TextField select size="small" label="Game" value={game} onChange={(e) => changeGame(e.target.value)} sx={{minWidth: 220}}>
                    {Object.entries(AI_GAME_LABELS).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                </TextField>
                {isFetching && <CircularProgress size={20}/>}
                {data?.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                        Saved {formatExact(data.updatedAt)}{data.updatedBy ? ` by ${data.updatedBy}` : ""}
                    </Typography>
                )}
            </Stack>
            {error && <Alert severity="error">{errorText(error)}</Alert>}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{width: "35%"}}>Player wording (any language)</TableCell>
                        <TableCell sx={{width: "25%"}}>Official English name</TableCell>
                        <TableCell>Note for the bot (optional)</TableCell>
                        <TableCell sx={{width: 48}}/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Typography variant="body2" color="text.secondary">No terms for this game yet.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <TextField fullWidth size="small" variant="standard" value={row.term}
                                           error={!row.term.trim()} placeholder="штурм босса / клановый штурм"
                                           onChange={(e) => update(index, "term", e.target.value)}/>
                            </TableCell>
                            <TableCell>
                                <TextField fullWidth size="small" variant="standard" value={row.en}
                                           error={!row.en.trim()} placeholder="Boss Strike"
                                           onChange={(e) => update(index, "en", e.target.value)}/>
                            </TableCell>
                            <TableCell>
                                <TextField fullWidth size="small" variant="standard" value={row.note}
                                           placeholder="clan event"
                                           onChange={(e) => update(index, "note", e.target.value)}/>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Remove term">
                                    <IconButton size="small" onClick={() => setRows((current) => current.filter((_, i) => i !== index))}>
                                        <DeleteOutlineIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Stack direction="row" spacing={1}>
                <Button startIcon={<AddIcon/>} onClick={() => setRows((current) => [...current, emptyTerm()])}>Add term</Button>
                <Button disabled={!dirty || saving} onClick={() => setRows(loaded)}>Discard changes</Button>
                <LoadingButton variant="contained" disabled={!dirty || invalid} loading={saving} onClick={save}>
                    Save glossary
                </LoadingButton>
            </Stack>
        </Stack>
    )
}
